import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Divider } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { omit, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import * as Yup from "yup";
import { OpeningHour, PhoneInput } from "../../../../clients/graphqlTypes.js";
import { OpeningHoursField } from "../../../commons/form-fields/OpeningHoursField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  PHONE_REG_EXP,
  SITE_REG_EXP,
} from "../../../commons/form-fields/helpers.js";
import { Stack } from "../../../commons/layout/Stack.js";
import { AddressField } from "../../addresses/AddressField.js";
import { AddressDetails__AddressFragment } from "../../addresses/Addresses.generated.js";
import { PhonesField } from "../../phones/PhonesField.js";
import { defaultPhoneType } from "../../phones/usePhoneTypes.js";
import { useModifyOrganisationProfile__ContactMutation } from "../Organisations.generated.js";
import { EditOrganisationProfileContactModal_OrganisationProfileFragment } from "./EditOrganisationProfileContactModal.generated.js";

type OpeningHours = {
  [day: string]: Pick<OpeningHour, "isOpen" | "from" | "to">; // day is in format "d0", "d1", etc.
};

interface FormValues {
  contactAddress?: AddressDetails__AddressFragment;
  contactPhones: PhoneInput[];
  contactWebsite?: string;
  contactEmail?: string;
  openingHours: OpeningHours | null;
}

interface Props {
  title?: string;
  organisationProfile: EditOrganisationProfileContactModal_OrganisationProfileFragment;
  handleClose: () => void;
  handleComplete?: (values: FormValues) => Promise<void> | void;
}

export const EditOrganisationProfileContactModal = ({
  title,
  organisationProfile,
  handleClose,
  handleComplete,
}: Props) => {
  const { t } = useTranslate([
    "OrganisationPublicProfile",
    "OrganisationProfile",
    "Global",
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [modifyProfile, { loading: isSubmitting }] =
    useModifyOrganisationProfile__ContactMutation({
      client,
    });

  const handleSubmit = async (values: FormValues) => {
    try {
      await modifyProfile({
        variables: {
          input: {
            slug: organisationProfile.slug,
            contactAddress: values.contactAddress
              ? {
                  ...omit(values.contactAddress, "__typename", "id"),
                  location: values.contactAddress.location
                    ? omit(values.contactAddress.location, "__typename")
                    : null,
                }
              : null,
            contactWebsite: values.contactWebsite,
            contactEmail: values.contactEmail,
            contactPhones: values.contactPhones
              .filter(p => p.number.trim())
              .map(p => omit(p, "__typename", "id")),
            openingHours: values.openingHours
              ? Object.entries(values.openingHours).map(([key, value]) => ({
                  day: parseInt(key.charAt(1)),
                  isOpen: value.isOpen,
                  from: value.from,
                  to: value.to,
                }))
              : null,
          },
        },
      });
      await handleComplete?.(values);
      handleClose();
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        contactEmail: Yup.string()
          .label(
            t("Email", {
              ns: "OrganisationProfile",
            })
          )
          .email()
          .trim(),
        contactWebsite: Yup.string()
          .label(
            t("Website", {
              ns: "OrganisationProfile",
            })
          )
          .matches(
            SITE_REG_EXP,
            t("Website is not valid", {
              ns: "OrganisationProfile",
            })
          ),
        contactAddress: Yup.object()
          .label(t("Address", { ns: "OrganisationProfile" }))
          .nullable(),
        contactPhones: Yup.array().of(
          Yup.object().shape({
            id: Yup.string(),
            main: Yup.boolean(),
            type: Yup.string(),
            number: Yup.string().matches(
              PHONE_REG_EXP,
              t("Phone number is not valid", {
                ns: "OrganisationProfile",
              })
            ),
          })
        ),
        openingHours: Yup.array(
          Yup.object({
            from: Yup.string().nullable(),
            to: Yup.string().nullable(),
            isOpen: Yup.boolean().required(),
          })
        ).nullable(),
      }),
    [t]
  );

  const initialValues: FormValues = {
    contactAddress: organisationProfile.contactAddress ?? undefined,
    contactWebsite: organisationProfile.contactWebsite ?? undefined,
    contactEmail: organisationProfile.contactEmail ?? undefined,
    contactPhones:
      organisationProfile.contactPhones?.length > 0
        ? organisationProfile.contactPhones
        : [defaultPhoneType],
    openingHours:
      organisationProfile.openingHours?.reduce<OpeningHours>(
        (acc, day, index) => {
          return {
            ...acc,
            [`d${index}`]: day,
          };
        },
        {}
      ) ?? null,
  };

  const formId = useMemo(() => uniqueId(), []);

  return (
    <Modal
      title={
        title ??
        t("Edit contact information", {
          ns: "OrganisationPublicProfile",
        })
      }
      dialogProps={{ maxWidth: "sm" }}
      actionButtons={[
        {
          label: t("Cancel", {
            ns: "Global",
          }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: isSubmitting },
        },
        {
          label: t("Save", {
            ns: "Global",
          }),
          buttonProps: {
            type: "submit",
            form: formId,
            loading: isSubmitting,
            disabled: isSubmitting,
          },
        },
      ]}
      handleClose={handleClose}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form id={formId}>
            <Stack flexDirection="column" width="100%">
              <TextField
                disabled={false}
                name="contactWebsite"
                label={t("Website", {
                  ns: "OrganisationProfile",
                })}
              />
              <TextField
                disabled={false}
                name="contactEmail"
                label={t("Email", {
                  ns: "OrganisationProfile",
                })}
                type="email"
              />
              <AddressField
                name="contactAddress"
                label={t("Address", {
                  ns: "OrganisationProfile",
                })}
                disabled={false}
              />
              <PhonesField
                name="contactPhones"
                buttonLabel={t("Add phone number", {
                  ns: "OrganisationProfile",
                })}
                value={values.contactPhones}
                disabled={false}
                autoSubmit={false}
              />
              <Divider />
              <OpeningHoursField name="openingHours" />
            </Stack>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
